import { useState, useEffect } from 'react';
import { Link, useParams } from 'wouter';
import { fetchBlogPostsByTag, formatDate, type BlogPost } from '@/lib/blog-api';
import { Clock, Tag, ArrowLeft, BookOpen } from 'lucide-react';
import { SEO } from "@/components/seo";

function getSSRTagData(currentTag: string | undefined): BlogPost[] | null {
  const data = (window as any).__BLOG_DATA__;
  if (data?.type === 'tag' && data?.tag === currentTag) {
    return data.posts;
  }
  return null;
}

export default function BlogTagPage() {
  const { tag } = useParams<{ tag: string }>();
  const ssrPosts = getSSRTagData(tag);
  const [posts, setPosts] = useState<BlogPost[]>(ssrPosts ?? []);
  const [loading, setLoading] = useState(!ssrPosts);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clean up SSR data to avoid stale state on SPA navigation
    if ((window as any).__BLOG_DATA__?.type === 'tag') {
      delete (window as any).__BLOG_DATA__;
      return;
    }

    async function loadPosts() {
      if (!tag) return;
      try {
        setLoading(true);
        setError(null);
        const data = await fetchBlogPostsByTag(tag, 20);
        setPosts(data.posts);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load posts');
      } finally {
        setLoading(false);
      }
    }
    loadPosts();
  }, [tag]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-pulse text-white/60">Loading posts...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Oops!</h1>
            <p className="text-white/60 mb-6">{error}</p>
            <Link href="/blog" className="text-blue-400 hover:underline">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const tagDisplay = tag ? tag.charAt(0).toUpperCase() + tag.slice(1) : '';

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title={`${tagDisplay} Articles | BounceBunny Blog`}
        description={`Browse ${posts.length} article${posts.length === 1 ? '' : 's'} about ${tagDisplay}. Training tips, insights, and advice from BounceBunny.`}
        canonical={`https://bouncebunny.app/blog/tag/${tag}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${tagDisplay} Articles`,
          description: `Articles tagged with ${tagDisplay} on BounceBunny Blog`,
          url: `https://bouncebunny.app/blog/tag/${tag}`,
          isPartOf: {
            '@type': 'Blog',
            name: 'BounceBunny Blog',
            url: 'https://bouncebunny.app/blog',
          },
        }}
      />
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Back link */}
        <Link
          href="/blog"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          All Posts
        </Link>

        {/* Header */}
        <header className="mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 mb-4">
            <Tag className="w-4 h-4" />
            <span className="font-medium">{tag}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold">
            Articles tagged "{tag}"
          </h1>
          <p className="text-white/60 mt-2">
            {posts.length} {posts.length === 1 ? 'article' : 'articles'} found
          </p>
        </header>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-white/30" />
            <h2 className="text-xl font-semibold mb-2">No posts found</h2>
            <p className="text-white/60 mb-6">
              No articles with this tag yet.
            </p>
            <Link href="/blog" className="text-blue-400 hover:underline">
              Browse all posts
            </Link>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <article
                key={post.id}
                className="group bg-white/5 rounded-xl overflow-hidden hover:bg-white/10 transition-colors"
              >
                <Link href={`/blog/${post.slug}`}>
                  {/* Cover Image */}
                  {post.coverImage ? (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.coverImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-blue-600/20 to-purple-600/20 flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-white/30" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5">
                    {/* Tags */}
                    {post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.slice(0, 2).map((t) => (
                          <span
                            key={t.slug}
                            className={`text-xs px-2 py-0.5 rounded ${
                              t.slug === tag
                                ? 'bg-blue-500/30 text-blue-300'
                                : 'bg-white/10 text-white/70'
                            }`}
                          >
                            {t.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-lg font-semibold mb-2 group-hover:text-blue-400 transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    {/* Brief */}
                    <p className="text-sm text-white/60 mb-4 line-clamp-2">
                      {post.brief}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-white/50">
                      <span>{formatDate(post.publishedAt)}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {post.readTimeInMinutes} min read
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
